import { Injectable } from '@angular/core';
import { HttpEvent } from '@angular/common/http';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { switchMap, withLatestFrom, tap, map, catchError, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { Update } from '@ngrx/entity';

import * as fromAuthStore from '@apps/auth/store';
import { IAuroraState } from '@apps/aurora.state';
import { NotificationService, FilesService, NotfGravity, NotifType } from '@common/services';
import { ResultDomain } from '@common/domain/result-domain';
import { Profile } from '@apps/auth/models';
import { getAllTags } from '@apps/cmdb/store';
import { joinAssets } from '@common/operators/join-assets';
import { Assetify } from '@common/domain/assetify';
import { AssetMiscDomainService } from '@apps/cmdb/domain/asset-misc-domain.service';
import { TypeAssetName } from '@apps/cmdb/cmdb.constants';
import { EnrichedAsset, Tag } from '@apps/cmdb/models';

import { JobListDomainService, JobMiscDomainService } from '../../domain';
import { getBckpJobsQueryString, getBckpJobsExportQueryString, getBckpJobsSinceUntil, getAllJobsByIdentifier, getBckpJobsStatsQueryString } from '../bckp.reducer';
import * as fromBckpJobsActions from './jobs.actions';
import { Job, JobDetails } from '../../domain/models';
import { BckpConstants } from '../../bckp.constants';
import { JobStatsApiService } from '../../api/job-stats-api.service';

@Injectable()
export class BckpJobEffects {
    private jobsCount: number;

    constructor(
        private actions: Actions,
        private jobListDomainService: JobListDomainService,
        private jobMiscDomainSevice: JobMiscDomainService,
        private store: Store<IAuroraState>,
        private notificationService: NotificationService,
        private filesService: FilesService,
        private assetMiscDomainService: AssetMiscDomainService,
        private jobStatsApiService: JobStatsApiService
    ) { }

    bckpJobsODataRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpJobsActions.BckpJobsODataRequested),
                    withLatestFrom(
                        this.store.select(getBckpJobsQueryString),
                        this.store.select(getAllTags),
                        (_, qs, allTags) => ({
                            qs,
                            allTags
                        })
                    ),
                    switchMap(({ qs, allTags }) =>
                        this.jobListDomainService.getAll(qs)
                            .pipe(
                                tap((dataLoaded: ResultDomain<Job>) => {
                                    this.jobsCount = dataLoaded.count;
                                }),
                                map((obj: ResultDomain<Job>): Job[] => obj.value),
                                joinAssets(this.assetMiscDomainService, TypeAssetName.HOST, allTags),
                                map((jobs: Job[]) =>
                                    fromBckpJobsActions.BckpJobsODataLoaded({
                                        dataLoaded: {
                                            value: jobs,
                                            count: this.jobsCount
                                        } as ResultDomain<Job>
                                    })
                                ),
                                catchError(() =>
                                    of(fromBckpJobsActions.BckpJobsODataFailed())
                                )
                            )
                    )
                )
    );

    bckpJobsODataFailed = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpJobsActions.BckpJobsODataFailed),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                'BACKUP.JOBS.NOTF.LIST.TITLE',
                                'BACKUP.JOBS.NOTF.LIST.MSGFAILED'
                            ],
                            NotfGravity.danger,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );

    bckpJobsExportRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpJobsActions.BckpJobsExportRequested),
                    withLatestFrom(this.store.select(getBckpJobsExportQueryString)),
                    switchMap(([params, qs]: [any, string]) =>
                        this.jobMiscDomainSevice.export(qs, params.format)
                    ),
                    map((response: HttpEvent<Blob>) =>
                        fromBckpJobsActions.BckpJobsExportSucceeded({ response })
                    )
                )
    );

    bckpJobsExportSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpJobsActions.BckpJobsExportSucceeded),
                    withLatestFrom(this.store.select(fromAuthStore.getProfile)),
                    tap(([params, profile]: [{ response: HttpEvent<Blob> }, Profile]) => {
                        this.filesService.saveFileFromApi(params.response, profile);
                    })
                ),
        { dispatch: false }
    );

    bckpJobsRestoreExportRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpJobsActions.BckpJobsRestoreExportRequested),
                    withLatestFrom(this.store.select(getBckpJobsExportQueryString)),
                    switchMap(([params, qs]: [any, string]) =>
                        this.jobMiscDomainSevice.restoresExport(qs, params.format)
                    ),
                    map((response: HttpEvent<Blob>) =>
                        fromBckpJobsActions.BckpJobsRestoreExportSucceeded({ response })
                    )
                )
    );

    bckpJobsRestoreExportSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpJobsActions.BckpJobsRestoreExportSucceeded),
                    withLatestFrom(this.store.select(fromAuthStore.getProfile)),
                    tap(([params, profile]: [{ response: HttpEvent<Blob> }, Profile]) => {
                        this.filesService.saveFileFromApi(params.response, profile);
                    })
                ),
        { dispatch: false }
    );

    bckpJobsFilterApplied = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpJobsActions.BckpJobsFilterApplied),
                    tap((params: any) => {
                        this.store.dispatch(fromBckpJobsActions.BckpJobsCleared());
                        this.store.dispatch(fromBckpJobsActions.BckpJobsODataInitialized({
                            scrollSize: BckpConstants.QSJobsTop,
                            filter: params.filter
                        }));
                    }),
                    tap(() =>
                        this.store.dispatch(fromBckpJobsActions.BckpJobsODataRequested())
                    )
                ),
        { dispatch: false }
    );

    bckpJobsDetailsRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpJobsActions.BckpJobsDetailsRequested),
                    withLatestFrom(this.store.select(getBckpJobsSinceUntil)),
                    switchMap(([params, qsSinceUntil]: [any, string]) =>
                        this.jobListDomainService.getJobDetails(params.documentId, qsSinceUntil)
                    ),
                    map((jobDetails: JobDetails) =>
                        fromBckpJobsActions.BckpJobsDetailsSucceeded({ jobDetails })
                    )
                )
    );

    bckpJobsTagAttachRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpJobsActions.BckpJobsTagAttachRequested),
                    switchMap((params) =>
                        this.assetMiscDomainService.attachTag(TypeAssetName.HOST, params.identifier, params.name, params.tagId)
                            .pipe(
                                map((enrichedAsset: EnrichedAsset) =>
                                    fromBckpJobsActions.BckpJobsTagAttachExecuted({ enrichedAsset })
                                )
                            )
                    )
                )
    );

    bckpJobsTagAttachExecuted = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpJobsActions.BckpJobsTagAttachExecuted),
                    switchMap((params) =>
                        combineLatest([
                            this.store.select(getAllJobsByIdentifier, { identifier: params.enrichedAsset.identifier }),
                            this.store.select(getAllTags)
                        ])
                            .pipe(
                                take(1),
                                tap(([jobs, tags]: [Job[], Tag[]]) => {
                                    jobs.forEach((job: Job) => {
                                        const updatedJob: Update<Job> = {
                                            id: job.storeId,
                                            changes: {
                                                tagIds: params.enrichedAsset.tagIds,
                                                tagFullNames: Assetify.BuildFullTagNames(params.enrichedAsset.tagIds, tags)
                                            }
                                        };
                                        this.store.dispatch(fromBckpJobsActions.BckpJobsTagAttachSucceeded({ updatedJob: updatedJob }));
                                    });
                                })
                            )
                    )
                ),
        { dispatch: false }
    );

    bckpJobsTagAttachSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpJobsActions.BckpJobsTagAttachSucceeded),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                'CMDB.ASSETS.ATTACH.TITLE',
                                'CMDB.ASSETS.ATTACH.SUCCEEDED'
                            ],
                            NotfGravity.success,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );

    bckpJobsTagDetachRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpJobsActions.BckpJobsTagDetachRequested),
                    switchMap((params) =>
                        this.assetMiscDomainService.detachTag(TypeAssetName.HOST, params.identifier, params.tagId)
                            .pipe(
                                map((enrichedAsset: EnrichedAsset) =>
                                    fromBckpJobsActions.BckpJobsTagDetachExecuted({ enrichedAsset })
                                )
                            )
                    )
                )
    );

    bckpJobsTagDetachExecuted = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpJobsActions.BckpJobsTagDetachExecuted),
                    switchMap((params) =>
                        combineLatest([
                            this.store.select(getAllJobsByIdentifier, { identifier: params.enrichedAsset.identifier }),
                            this.store.select(getAllTags)
                        ])
                            .pipe(
                                take(1),
                                tap(([jobs, tags]: [Job[], Tag[]]) => {
                                    jobs.forEach((job: Job) => {
                                        const updatedJob: Update<Job> = {
                                            id: job.storeId,
                                            changes: {
                                                tagIds: params.enrichedAsset.tagIds,
                                                tagFullNames: Assetify.BuildFullTagNames(params.enrichedAsset.tagIds, tags)
                                            }
                                        };
                                        this.store.dispatch(fromBckpJobsActions.BckpJobsTagDetachSucceeded({ updatedJob: updatedJob }));
                                    });
                                })
                            )
                    )
                ),
        { dispatch: false }
    );

    bckpJobsTagDetachSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpJobsActions.BckpJobsTagDetachSucceeded),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                'CMDB.ASSETS.DETACH.TITLE',
                                'CMDB.ASSETS.DETACH.SUCCEEDED'
                            ],
                            NotfGravity.success,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );

    bckpJobsStatsRequested = createEffect(() =>
        this.actions.pipe(
            ofType(fromBckpJobsActions.BckpJobsODataRequested),
            withLatestFrom(this.store.select(getBckpJobsStatsQueryString)),
            switchMap(([_, qs]) =>
                this.jobStatsApiService.getJobStats(qs ?? '').pipe(
                    map(stats => fromBckpJobsActions.BckpJobsStatsLoaded({ stats })),
                    catchError(() => of(fromBckpJobsActions.BckpJobsStatsFailed()))
                )
            )
        )
    );

}
